import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 Plus, Trash2
} from 'lucide-react';
import OELCodeEditor, {
 OELContext
} from '../components/OELCodeEditor';
import {
    ChooseStep, ChooseCase, ChooseRun, JsonOpcodeStep, FlowDefinition
} from 'builders/builderOpcode/jsonMode/types';

const RunRefEditor = ({
    run,
    onChange,
    definitions,
    flows,
}: {
    run: ChooseRun;
    onChange: (run: ChooseRun) => void;
    definitions: JsonOpcodeStep[];
    flows: FlowDefinition[];
}) => {
    const refs = run.kind === 'step'
        ? definitions.map(d => d.id)
        : flows.map(f => f.id);

    return (
        <div className="flex gap-2 items-end">
            <div className="space-y-1">
                <Label className="text-xs">Run Type</Label>
                <Select
                    value={run.kind}
                    onValueChange={(val) => onChange({
                        ...run,
                        kind: val as 'step' | 'flow',
                        ref: ''
                    } as ChooseRun)}
                >
                    <SelectTrigger className="w-[120px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="step">Definition</SelectItem>
                        <SelectItem value="flow">Flow</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="flex-1 space-y-1">
                <Label className="text-xs">Reference</Label>
                {refs.length > 0 ? (
                    <Select
                        value={run.ref || ''}
                        onValueChange={(val) => onChange({
 ...run,
ref: val
} as ChooseRun)}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select reference" />
                        </SelectTrigger>
                        <SelectContent>
                            {refs.map((id) => (
                                <SelectItem key={id} value={id}>{id}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <Input
                        value={run.ref || ''}
                        onChange={(e) => onChange({
 ...run,
ref: e.target.value
} as ChooseRun)}
                        placeholder={`Enter ${run.kind} ID`}
                        className="font-mono text-sm"
                    />
                )}
            </div>
        </div>
    );
};

const V2StepChoose = ({
    step,
    onStepChange,
    definitions = [],
    flows = [],
    oelContext,
}: {
    step: ChooseStep;
    onStepChange: (step: ChooseStep) => void;
    definitions?: JsonOpcodeStep[];
    flows?: FlowDefinition[];
    oelContext?: OELContext;
}) => {
    const ctx = oelContext || {
 inputs: [],
previousStepIds: []
};
    const cases = step.choose?.cases || [];

    const handleAddCase = () => {
        const newCase: ChooseCase = {
            when: '',
            run: {
 kind: 'step',
ref: ''
}
        };
        onStepChange({
            ...step,
            choose: {
                ...step.choose,
                cases: [...cases, newCase]
            }
        });
    };

    const handleRemoveCase = (index: number) => {
        onStepChange({
            ...step,
            choose: {
                ...step.choose,
                cases: cases.filter((_, i) => i !== index)
            }
        });
    };

    const handleCaseChange = (index: number, updated: Partial<ChooseCase>) => {
        const newCases = cases.map((c, i) => i === index ? {
 ...c,
...updated
} : c);
        onStepChange({
            ...step,
            choose: {
 ...step.choose,
cases: newCases
}
        });
    };

    const handleDefaultChange = (run: ChooseRun | undefined) => {
        const choose = {
 ...step.choose,
cases
};
        if (run) {
            choose.default = run;
        } else {
            delete choose.default;
        }
        onStepChange({
 ...step,
choose
});
    };

    return (
        <div className="space-y-4">
            <Label className="text-sm font-semibold">Cases</Label>
            <p className="text-xs text-muted-foreground">
                Each case evaluates a condition. The first matching case runs its referenced definition or flow.
            </p>

            {cases.map((c, index) => (
                <div key={index} className="p-3 border rounded-md space-y-3">
                    <div className="flex justify-between items-center">
                        <Label className="text-sm font-medium">Case {index + 1}</Label>
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRemoveCase(index)}
                        >
                            <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                    </div>

                    <OELCodeEditor
                        label="When (Condition)"
                        value={c.when}
                        onChange={(val) => handleCaseChange(index, {
 when: val
})}
                        placeholder="@input.type == 'urgent'"
                        required
                        oelContext={ctx}
                        templateMode={false}
                    />

                    <RunRefEditor
                        run={c.run}
                        onChange={(run) => handleCaseChange(index, {
 run
})}
                        definitions={definitions}
                        flows={flows}
                    />
                </div>
            ))}

            <Button type="button" size="sm" variant="outline" onClick={handleAddCase} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Case
            </Button>

            {/* Default */}
            <div className="p-3 border rounded-md space-y-3">
                <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">Default (Fallback)</Label>
                    {step.choose?.default ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDefaultChange(undefined)}
                        >
                            Remove
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDefaultChange({
 kind: 'step',
ref: ''
})}
                        >
                            Add Default
                        </Button>
                    )}
                </div>
                {step.choose?.default && (
                    <RunRefEditor
                        run={step.choose.default}
                        onChange={(run) => handleDefaultChange(run)}
                        definitions={definitions}
                        flows={flows}
                    />
                )}
            </div>
        </div>
    );
};

export default V2StepChoose;
